import { useEffect } from 'react';
import { useBrowserNotificationPermission } from '../hooks/notifications/useBrowserNotificationPermission';
import { useContextStore } from '../hooks/context/useContextStore';

// Extend Window interface for FCM debug logs (used in E2E tests)
declare global {
  interface Window {
    __fcmLogs?: string[];
  }
}

/**
 * Auto-initializes browser notifications when user is authenticated
 * This component should be mounted inside AuthChecker to ensure user is logged in
 *
 * Note: Device is already registered during sign-in with device info.
 * This component only updates the device with FCM token when notifications are enabled.
 */
const BrowserNotificationInitializer = () => {
  const { clientUser } = useContextStore();
  const { initializeBrowserNotifications } = useBrowserNotificationPermission();

  useEffect(() => {
    // Debug logging for E2E tests
    console.log('[BrowserNotificationInitializer] Component mounted');
    if (!window.__fcmLogs) {
      window.__fcmLogs = [];
    }
    window.__fcmLogs.push('BrowserNotificationInitializer mounted');

    // Only initialize if user is authenticated
    if (clientUser?.email) {
      console.log(
        '[BrowserNotificationInitializer] User authenticated, initializing FCM',
        clientUser.email
      );
      window.__fcmLogs?.push(`User authenticated: ${clientUser.email}`);

      // Auto-initialize browser notifications
      // This will update the device (already registered during sign-in) with FCM token
      // FCM errors are non-critical and expected in local development
      initializeBrowserNotifications()
        .then(() => {
          console.log('[BrowserNotificationInitializer] Initialization complete');
          window.__fcmLogs?.push('Initialization complete');
        })
        .catch(error => {
          // Log as warning instead of error - FCM failures are non-critical
          // and expected in local development environments
          console.warn(
            '[BrowserNotificationInitializer] Initialization failed (non-critical):',
            error?.message || error
          );
          window.__fcmLogs?.push(
            `Initialization failed (non-critical): ${error?.message || error}`
          );
        });
    } else {
      console.log('[BrowserNotificationInitializer] No user authenticated yet');
      window.__fcmLogs?.push('No user authenticated');
    }
  }, [clientUser, initializeBrowserNotifications]);

  // This component renders nothing
  return null;
};

export default BrowserNotificationInitializer;
